import Dinero from 'dinero.js';

import { hasMixedCurrenciesForInvoice, resolveSelectedCurrencyFromStatements } from './statementSelection';

export const getDineroAmount = (amount, currency, defaultCurrency) => {
    return Dinero({
        amount: Math.round((amount || 0) * 100),
        currency: currency || defaultCurrency,
    });
};

export const getDisplayedStatementAmount = (statement, defaultCurrency) => {
    const transactionCurrency = statement?.Transaction?.Currency || statement?.Currency || defaultCurrency;
    const exchangeRate = Number(statement?.ExchangeRate);
    // Same rule as statements and invoices: converted whenever an exchange rate is set, 1 included
    const hasConvertedAmount = Boolean(
        transactionCurrency &&
            defaultCurrency &&
            transactionCurrency !== defaultCurrency &&
            statement?.AmountDef !== undefined &&
            statement?.AmountDef !== null &&
            exchangeRate > 0
    );

    return {
        amount: hasConvertedAmount ? statement?.AmountDef ?? 0 : statement?.Amount ?? 0,
        currency: hasConvertedAmount ? defaultCurrency : transactionCurrency,
        isConverted: hasConvertedAmount,
        originalAmount: statement?.Amount ?? 0,
        originalCurrency: transactionCurrency,
    };
};

export const groupDisplayedStatementTotalsByCurrency = (statements, defaultCurrency) => {
    if (!statements || statements.length === 0) {
        return [];
    }

    const totalsByCurrency = {};

    for (const statement of statements) {
        const { amount, currency } = getDisplayedStatementAmount(statement, defaultCurrency);
        const resolvedCurrency = currency || defaultCurrency;

        if (!totalsByCurrency[resolvedCurrency]) {
            totalsByCurrency[resolvedCurrency] = getDineroAmount(0, resolvedCurrency, defaultCurrency);
        }

        totalsByCurrency[resolvedCurrency] = totalsByCurrency[resolvedCurrency].add(
            getDineroAmount(amount, resolvedCurrency, defaultCurrency)
        );
    }

    return Object.entries(totalsByCurrency)
        .sort(([currencyA], [currencyB]) => {
            if (currencyA === defaultCurrency) {
                return -1;
            }
            if (currencyB === defaultCurrency) {
                return 1;
            }
            return currencyA.localeCompare(currencyB);
        })
        .map(([Currency, Dinero]) => ({
            Currency,
            Dinero,
        }));
};

// Total in the currency an invoice of these statements would get: converted amounts only
// when that is the default currency, no total when the currencies are mixed
export const sumTaxedStatementsTotal = (statements, defaultCurrency, numberFormat) => {
    if (!statements || statements.length === 0) {
        return '';
    }
    if (hasMixedCurrenciesForInvoice(statements, defaultCurrency)) {
        return '';
    }

    const currency = resolveSelectedCurrencyFromStatements(statements, defaultCurrency);
    const isDefaultCurrency = currency === defaultCurrency;
    let total = getDineroAmount(0, currency, defaultCurrency);

    for (const statement of statements) {
        const amountToSum =
            isDefaultCurrency && statement.TaxedAmountDef ? statement.TaxedAmountDef : statement.TaxedAmount;
        total = total.add(getDineroAmount(amountToSum, currency, defaultCurrency));
    }

    return numberFormat ? total.toFormat(numberFormat) : total;
};
